import React, { useRef } from 'react';
import { Link } from 'react-router';
import {
    stripTrackingParamsFromExternalUrl,
    useTracker,
} from '../../../../../tracker';
import { useImpression } from '../../../../../tracker/src/react-bindings';
import { Image } from '@rocco/components/image';

interface AnalyticsAmplifierTileProps {
    professionalId: number;
    image: string; // Main background image
    logo: string; // Logo image
    name: string;
    subtitle: string;
    /** Outbound professional website URL; callers should attach `__apid` when available. */
    website: string;
    backgroundColor: string;
}

export const AnalyticsAmplifierTile: React.FC<AnalyticsAmplifierTileProps> = ({
    professionalId,
    image,
    logo,
    name,
    subtitle,
    website,
    backgroundColor,
}) => {
    const ref = useRef<HTMLAnchorElement>(null);
    const tracker = useTracker();

    // Track impression when the card is visible
    useImpression({
        onImpression: async (url: URL) => {
            await tracker.log('AnalyticsAmplifierImpression', {
                url: url,
                targetTracker: ['archiproTracker', 'ga4Tracker'],
                data: {
                    // Matches the click below. This previously sent a lowercase
                    // `professionalID` and a `type`, neither of which is a
                    // column LoggingService flattens, so every impression
                    // landed with no professional on it — leaving a sold
                    // placement with an uncomputable CTR.
                    LibraryItemID: professionalId,
                    ProfessionalID: professionalId,
                    Source: 'AnalyticsAmplifier',
                    ExtraData: JSON.stringify({
                        professionalId: professionalId,
                        type: 'AnalyticsAmplifier',
                    }),
                },
                ga4Data: {
                    professionalId: professionalId,
                },
            });
        },
        ref,
    });

    const handleClick = () => {
        if (!website) {
            return;
        }

        // Match Website referral: keep a destination URL even when parse/strip fails.
        const externalURL =
            stripTrackingParamsFromExternalUrl(website) ?? website;
        void tracker.log('AnalyticsAmplifierClick', {
            url: new URL(window.location.href),
            targetTracker: ['archiproTracker', 'ga4Tracker'],
            data: {
                // LoggingService only applies ProfessionalID after LibraryItem resolve.
                LibraryItemID: professionalId,
                ProfessionalID: professionalId,
                ExternalURL: externalURL,
                // Snowflake MERGED_PROFESSIONAL_ID fallback when professional_id is 0.
                ExtraData: JSON.stringify({
                    professionalId: professionalId,
                }),
            },
            ga4Data: {
                professionalId: professionalId,
            },
        });
    };
    return (
        // Mirrors ProductTile's shape so the amplifier reads as a peer of the
        // tiles around it rather than a full-bleed banner: the creative is
        // capped to the grid's media aspect, and the professional is named
        // underneath in the product tile's own title/supplier type.
        <Link
            to={website}
            target="_blank"
            className="group flex h-full w-full flex-col gap-2.5"
            onClick={handleClick}
            ref={ref}
        >
            {/* Same frame and corner radius as the tiles it sits between. */}
            <div className="@container relative aspect-tile-product-tall w-full overflow-hidden rounded-tile shadow-lg">
                {/* Background Image */}
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${image})` }}
                />
                {/* Overlay */}
                <div
                    className="absolute inset-0 top-0 left-0 h-full w-full"
                    style={{
                        background:
                            'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 65.37%, rgba(0, 0, 0, 0.40) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.00) 63.04%, rgba(0, 0, 0, 0.40) 90.27%), rgba(0, 0, 0, 0.20)',
                    }}
                />
                {/* Logo (top-left) */}
                {/* Sized for the 3-4 column tile; on narrower Display
                    settings the fixed badge and name no longer fit the
                    creative, so both rows zoom down with the tile. */}
                <div className="absolute top-2 left-2.5 flex w-full items-center gap-2 md:top-4 md:left-4 md:w-[calc(100%-1.5rem)] md:gap-4 @max-[240px]:[zoom:0.85] @max-[200px]:[zoom:0.7]">
                    <div
                        className="flex size-12.5 items-center justify-center overflow-hidden rounded-full p-0.5 md:size-23"
                        style={{
                            backgroundColor: backgroundColor || 'white',
                        }}
                    >
                        <Image
                            src={logo}
                            alt={`${name} logo`}
                            size="professionalLogo"
                        />
                    </div>
                    {/* Mobile */}
                    <span className="w-24 font-display leading-[1.1] font-medium text-white md:w-[60%] md:max-w-42 md:pr-1 md:text-5 md:font-medium">
                        {name}
                    </span>
                </div>
                {/* Content (bottom) */}
                <div className="absolute right-3 bottom-4 left-3.5 z-10 md:right-3 md:bottom-8 md:left-5 @max-[240px]:[zoom:0.85] @max-[200px]:[zoom:0.7]">
                    <span className="flex w-42 max-w-full font-display text-3.5 leading-[1.1] font-medium text-white md:w-62 md:text-5 md:font-medium">
                        {subtitle}
                    </span>
                </div>
            </div>
            {/* Name + role below the creative, in the product tile's own
                title/supplier treatment. Kept in sync with ProductTile's
                portrait branch. */}
            <div className="flex flex-col">
                <span className="overflow-hidden font-medium text-ellipsis text-[#323232] max-md:line-clamp-2 max-md:text-[0.75rem] max-md:leading-[1.2] md:line-clamp-1 md:text-[1rem] md:leading-[1.3]">
                    {name}
                </span>
                <span className="line-clamp-1 overflow-hidden font-normal text-ellipsis max-md:text-[0.75rem] max-md:leading-[1.3] max-md:text-gray-500 md:text-[1rem] md:leading-[1.3] md:text-[#767676]">
                    Manufacturer supplier
                </span>
            </div>
        </Link>
    );
};
